import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Satellite, LogIn, Eye, EyeOff, ShieldCheck, KeyRound, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { api, setAuth, roleHome } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  // ── Forgot password state ──
  // mode: 'login' | 'forgot_step1' | 'forgot_step2' | 'forgot_step3' | 'done'
  const [mode, setMode] = useState("login");
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPw, setFpNewPw] = useState("");
  const [fpConfirmPw, setFpConfirmPw] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);
  const [otpMsg, setOtpMsg] = useState("");

  const resetForgot = () => {
    setMode("login");
    setFpEmail(""); setFpOtp("");
    setFpNewPw(""); setFpConfirmPw("");
    setOtpMsg(""); setShowNewPw(false);
  };

  // ── Login submit ──
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { login: email, password });
      if (data.user.role !== "superadmin") {
        toast.error("Please log in from your operator's specific website.");
        return;
      }
      setAuth(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      navigate(roleHome(data.user.role), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  // ── Step 1: Send OTP ──
  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!fpEmail.trim()) { toast.error("Enter your email or mobile"); return; }
    setFpLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { mobile: fpEmail.trim() });
      setOtpMsg(data.message);
      setMode("forgot_step2");
      toast.success("OTP sent!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally { setFpLoading(false); }
  };

  // ── Step 2: Proceed to set password ──
  const verifyOtp = (e) => {
    e.preventDefault();
    if (!fpOtp.trim()) { toast.error("Enter the OTP"); return; }
    setMode("forgot_step3");
  };

  // ── Step 3: Reset password ──
  const resetPassword = async (e) => {
    e.preventDefault();
    if (fpNewPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (fpNewPw !== fpConfirmPw) { toast.error("Passwords do not match"); return; }
    setFpLoading(true);
    try {
      await api.post("/auth/reset-password", {
        mobile: fpEmail.trim(),
        otp: fpOtp.trim(),
        new_password: fpNewPw,
      });
      setMode("done");
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. Check your OTP.");
      setMode("forgot_step2");
    } finally { setFpLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left panel */}
      <div className="relative hidden md:flex items-center justify-center bg-brand-nav text-white p-12 overflow-hidden">
        <img alt="" src="https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHx0ZWxlY29tJTIwc2VydmVyJTIwaW5mcmFzdHJ1Y3R1cmV8ZW58MHx8fHwxNzc2OTI2MTUzfDA&ixlib=rb-4.1.0&q=85" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-nav/90 via-brand-primary/70 to-brand-accent/60" />
        <div className="relative max-w-md">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white/15 backdrop-blur flex items-center justify-center">
              <Satellite className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl">DishLCO</span>
          </div>
          <h1 className="mt-12 font-heading font-black text-5xl tracking-tighter leading-[1.05]">Super Admin Portal</h1>
          <p className="mt-6 text-white/80 leading-relaxed">Secure command plane for DishLCO system administration and tenant management.</p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="md:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-brand-primary to-brand-accent flex items-center justify-center">
              <Satellite className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-brand-nav">DishLCO</span>
          </Link>

          {/* Step indicator */}
          {["forgot_step1", "forgot_step2", "forgot_step3"].includes(mode) && (
            <div className="flex items-center gap-2 mb-6">
              {[1, 2, 3].map((step, i) => {
                const current = { forgot_step1: 1, forgot_step2: 2, forgot_step3: 3 }[mode];
                return (
                  <React.Fragment key={step}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step < current ? "bg-green-500 text-white" :
                        step === current ? "bg-brand-primary text-white" :
                          "bg-slate-100 text-slate-400"
                      }`}>
                      {step < current ? <CheckCircle2 className="w-4 h-4" /> : step}
                    </div>
                    {i < 2 && <div className={`flex-1 h-0.5 transition-all ${step < current ? "bg-green-400" : "bg-slate-200"}`} />}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {/* Headings */}
          {mode === "login" && <><h2 className="font-heading font-bold text-3xl text-brand-nav tracking-tight">Sign in</h2><p className="text-slate-500 mt-2">Welcome back — pick up where you left off.</p></>}
          {mode === "forgot_step1" && <><h2 className="font-heading font-bold text-3xl text-brand-nav tracking-tight">Forgot Password</h2><p className="text-slate-500 mt-2">Enter your registered email or mobile</p></>}
          {mode === "forgot_step2" && <><h2 className="font-heading font-bold text-3xl text-brand-nav tracking-tight">Enter OTP</h2><p className="text-slate-500 mt-2">Check your mobile / email for the code</p></>}
          {mode === "forgot_step3" && <><h2 className="font-heading font-bold text-3xl text-brand-nav tracking-tight">New Password</h2><p className="text-slate-500 mt-2">Choose a strong password</p></>}
          {mode === "done" && <><h2 className="font-heading font-bold text-3xl text-brand-nav tracking-tight">Password Reset!</h2><p className="text-slate-500 mt-2">You can now sign in with your new password.</p></>}

          {/* ── LOGIN FORM ── */}
          {mode === "login" && (
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                  data-testid="login-email-input"
                  className="w-full border border-slate-200 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
                  placeholder="you@company.com" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-slate-700">Password</label>
                  <button type="button" onClick={() => { setFpEmail(email); setMode("forgot_step1"); }}
                    className="text-xs font-semibold text-brand-primary hover:underline">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input value={password} onChange={(e) => setPassword(e.target.value)} type={showPw ? "text" : "password"} required
                    data-testid="login-password-input"
                    className="w-full border border-slate-200 rounded-md px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
                    placeholder="••••••••" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand-primary">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="login-submit-button"
                className="w-full bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold py-2.5 rounded-md shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? "Signing in..." : <><LogIn className="w-4 h-4" /> Sign in</>}
              </button>
            </form>
          )}

          {/* ── STEP 1: Enter email/mobile ── */}
          {mode === "forgot_step1" && (
            <form className="mt-8 space-y-5" onSubmit={sendOtp}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email or Mobile</label>
                <input value={fpEmail} onChange={e => setFpEmail(e.target.value)} type="text" required autoFocus
                  className="w-full border border-slate-200 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
                  placeholder="you@company.com" />
              </div>
              <button type="submit" disabled={fpLoading}
                className="w-full flex justify-center items-center gap-2 py-2.5 rounded-md text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover transition-all disabled:opacity-60">
                {fpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Send OTP
              </button>
              <button type="button" onClick={resetForgot}
                className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
              </button>
            </form>
          )}

          {/* ── STEP 2: OTP ── */}
          {mode === "forgot_step2" && (
            <form className="mt-8 space-y-5" onSubmit={verifyOtp}>
              {otpMsg && <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-700 font-medium">{otpMsg}</div>}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">OTP Code</label>
                <input value={fpOtp} onChange={e => setFpOtp(e.target.value)} type="text"
                  inputMode="numeric" maxLength={6} required autoFocus
                  className="w-full text-center text-2xl font-heading font-bold tracking-[0.5em] border border-slate-200 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
                  placeholder="· · · ·" />
              </div>
              <button type="submit"
                className="w-full flex justify-center items-center gap-2 py-2.5 rounded-md text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover transition-all">
                <KeyRound className="w-4 h-4" /> Verify OTP
              </button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => setMode("forgot_step1")} className="text-slate-500 hover:text-slate-700 flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3" /> Change email
                </button>
                <button type="button" onClick={sendOtp} disabled={fpLoading} className="text-brand-primary font-semibold hover:underline disabled:opacity-50">
                  {fpLoading ? "Sending..." : "Resend OTP"}
                </button>
              </div>
            </form>
          )}

          {/* ── STEP 3: New password ── */}
          {mode === "forgot_step3" && (
            <form className="mt-8 space-y-5" onSubmit={resetPassword}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
                <div className="relative">
                  <input value={fpNewPw} onChange={e => setFpNewPw(e.target.value)} type={showNewPw ? "text" : "password"}
                    required autoFocus minLength={6}
                    className="w-full border border-slate-200 rounded-md px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
                    placeholder="At least 6 characters" />
                  <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand-primary">
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm Password</label>
                <input value={fpConfirmPw} onChange={e => setFpConfirmPw(e.target.value)} type="password" required
                  className={`w-full border rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 transition-all ${fpConfirmPw && fpConfirmPw !== fpNewPw
                      ? "border-red-300 focus:ring-red-300"
                      : "border-slate-200 focus:ring-brand-primary/30 focus:border-brand-primary"
                    }`}
                  placeholder="Re-enter password" />
                {fpConfirmPw && fpConfirmPw !== fpNewPw && <p className="text-xs text-red-500 mt-1">Passwords do not match</p>}
              </div>
              <button type="submit" disabled={fpLoading || Boolean(fpConfirmPw && fpConfirmPw !== fpNewPw)}
                className="w-full flex justify-center items-center gap-2 py-2.5 rounded-md text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover transition-all disabled:opacity-60">
                {fpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Reset Password
              </button>
            </form>
          )}

          {/* ── DONE ── */}
          {mode === "done" && (
            <div className="mt-8 text-center space-y-5">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-9 h-9 text-green-500" />
              </div>
              <p className="text-slate-600">Your password has been changed. Sign in with your new credentials.</p>
              <button onClick={resetForgot}
                className="w-full flex justify-center items-center gap-2 py-2.5 rounded-md text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover transition-all">
                <LogIn className="w-4 h-4" /> Back to Sign In
              </button>
            </div>
          )}

          {mode === "login" && (
            <p className="text-sm text-slate-500 mt-6 text-center">
              No account? <Link to="/register" className="text-brand-primary font-semibold hover:underline" data-testid="login-to-register">Register as an LCO →</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
